/**
 * 防抖异步执行工具类
 * 实现防抖机制，确保异步函数按顺序执行，只保留最后一个等待执行的函数
 */
export class DebounceAsync {
  constructor() {
    this.debounceTimer = null;
    this.isExecuting = false;
    this.pendingAsyncFn = null;
  }

  /**
   * 防抖异步执行方法
   * @param {Function} asyncFn - 要执行的异步函数
   * @param {number} delay - 防抖延迟时间，默认100ms
   * @returns {Promise} 返回执行结果的Promise
   */
  debounce(asyncFn, delay = 100) {
    // 如果已经有防抖定时器，清除它
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // 将新的异步函数设置为待执行的函数
    this.pendingAsyncFn = asyncFn;

    return new Promise((resolve, reject) => {
      // 设置防抖定时器
      this.debounceTimer = setTimeout(async () => {
        // 如果当前有异步函数正在执行，等待它完成
        if (this.isExecuting) {
          // 等待当前执行完成
          await this.waitForExecutionComplete();
        }

        // 检查是否还有待执行的函数（可能被新的调用覆盖）
        if (this.pendingAsyncFn === asyncFn) {
          this.isExecuting = true;
          this.pendingAsyncFn = null;

          try {
            const result = await asyncFn();
            resolve(result);
          } catch (error) {
            reject(error);
          } finally {
            this.isExecuting = false;
          }
        } else {
          // 如果待执行函数已被覆盖，说明有新的调用，递归执行
          this.debounce(this.pendingAsyncFn, delay).then(resolve).catch(reject);
        }
      }, delay);
    });
  }

  /**
   * 等待当前执行完成
   * @returns {Promise} 等待Promise
   */
  waitForExecutionComplete() {
    return new Promise((resolve) => {
      const checkExecution = () => {
        if (!this.isExecuting) {
          resolve();
        } else {
          setTimeout(checkExecution, 10);
        }
      };
      checkExecution();
    });
  }

  /**
   * 清理资源，取消待执行的函数
   */
  cleanup() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.pendingAsyncFn = null;
  }
}

/**
 * 创建防抖异步执行实例的工厂函数
 * @returns {DebounceAsync} 防抖异步执行实例
 */
export function createDebounceAsync() {
  return new DebounceAsync();
}

/**
 * 防抖异步执行的高阶函数
 * 直接返回一个防抖异步执行函数，使用更简单
 * @param {number} delay - 防抖延迟时间，默认100ms
 * @returns {Function} 防抖异步执行函数
 */
export function createDebounceAsyncFn(delay = 100) {
  const debounceAsync = new DebounceAsync();

  return (asyncFn) => {
    return debounceAsync.debounce(asyncFn, delay);
  };
}

/**
 * 队列异步执行工具类
 * 确保异步函数按顺序执行，只有前面的函数执行成功后才会执行后面的
 */
export class QueueAsync {
  constructor() {
    this.queue = [];
    this.isExecuting = false;
  }

  /**
   * 添加异步函数到队列
   * @param {Function} asyncFn - 要执行的异步函数
   * @param {string} id - 可选的函数标识，用于去重
   * @returns {Promise} 返回执行结果的Promise
   */
  enqueue(asyncFn, id = null) {
    return new Promise((resolve, reject) => {
      const task = {
        id,
        asyncFn,
        resolve,
        reject,
        timestamp: Date.now(),
      };

      // 如果提供了id，检查是否已存在相同id的任务
      if (id) {
        const existingIndex = this.queue.findIndex((item) => item.id === id);
        if (existingIndex !== -1) {
          // 替换已存在的任务
          this.queue[existingIndex] = task;
        } else {
          // 添加新任务
          this.queue.push(task);
        }
      } else {
        // 没有id，直接添加
        this.queue.push(task);
      }

      // 开始执行队列
      this.executeNext();
    });
  }

  /**
   * 执行队列中的下一个任务
   */
  async executeNext() {
    // 如果正在执行或队列为空，直接返回
    if (this.isExecuting || this.queue.length === 0) {
      return;
    }

    this.isExecuting = true;
    const task = this.queue.shift();

    try {
      console.log(
        `执行队列任务 ${task.id || "anonymous"}, 队列长度: ${this.queue.length}`
      );
      const result = await task.asyncFn();
      task.resolve(result);
      console.log(`队列任务 ${task.id || "anonymous"} 执行成功`);
    } catch (error) {
      console.error(`队列任务 ${task.id || "anonymous"} 执行失败:`, error);
      task.reject(error);
    } finally {
      this.isExecuting = false;
      // 继续执行下一个任务
      this.executeNext();
    }
  }

  /**
   * 清空队列
   * @param {string} reason - 清空原因
   */
  clearQueue(reason = "手动清空") {
    console.log(`清空队列: ${reason}, 清空任务数: ${this.queue.length}`);
    this.queue.forEach((task) => {
      task.reject(new Error(`队列被清空: ${reason}`));
    });
    this.queue = [];
  }

  /**
   * 获取队列状态
   * @returns {Object} 队列状态信息
   */
  getQueueStatus() {
    return {
      isExecuting: this.isExecuting,
      queueLength: this.queue.length,
      queue: this.queue.map((task) => ({
        id: task.id,
        timestamp: task.timestamp,
      })),
    };
  }

  /**
   * 等待队列执行完成
   * @returns {Promise} 等待Promise
   */
  waitForQueueComplete() {
    return new Promise((resolve) => {
      const checkQueue = () => {
        if (!this.isExecuting && this.queue.length === 0) {
          resolve();
        } else {
          setTimeout(checkQueue, 10);
        }
      };
      checkQueue();
    });
  }
}

/**
 * 创建队列异步执行实例的工厂函数
 * @returns {QueueAsync} 队列异步执行实例
 */
export function createQueueAsync() {
  return new QueueAsync();
}

/**
 * 队列异步执行的高阶函数
 * 直接返回一个队列异步执行函数，使用更简单
 * @returns {Function} 队列异步执行函数
 */
export function createQueueAsyncFn() {
  const queueAsync = new QueueAsync();

  return (asyncFn, id) => {
    return queueAsync.enqueue(asyncFn, id);
  };
}
